import type { ApiErrorBody, ErrorCode, Suggestion } from './types'

/**
 * Единственное место, где приложение ходит в сеть.
 *
 * Ошибка у API всегда одной формы: {"error":{"code","message"}}. code выбирает экран,
 * message показывается человеку как есть — на сервере он написан так, чтобы объяснять
 * и что произошло, и что делать. Придумывать свой текст поверх серверного не нужно:
 * два источника правды на одну ошибку расходятся в первый же месяц.
 */

/** Префикс тот же и в разработке (его снимает прокси Vite), и за обратным прокси в бою. */
const BASE = '/api'

export class ApiError extends Error {
  readonly code: ErrorCode
  readonly status: number
  readonly details?: Record<string, string>
  /** Приходит вместе со slot_taken — альтернативы для экрана 2.7. */
  readonly suggestions?: Suggestion[]

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.error.code
    this.details = body.error.details
    this.suggestions = body.suggestions
  }
}

/** Сеть не отвечает — это не ошибка API, и текст должен быть другим. */
export class NetworkError extends Error {
  constructor() {
    super('Не удалось связаться с сервером. Проверьте подключение и повторите попытку.')
    this.name = 'NetworkError'
  }
}

type Query = Record<string, string | number | boolean | undefined | null>

function withQuery(path: string, query?: Query): string {
  if (!query) return BASE + path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return BASE + path + (qs ? `?${qs}` : '')
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Query
  signal?: AbortSignal
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options

  let response: Response
  try {
    response = await fetch(withQuery(path, query), {
      method,
      // Сессия живёт в HTTP-only куке, не в заголовке: её нельзя прочитать из JS,
      // и отозвать её сервер может мгновенно.
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new NetworkError()
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const payload: unknown = text ? safeParse(text) : undefined

  if (!response.ok) {
    if (isApiErrorBody(payload)) throw new ApiError(response.status, payload)
    // Сервер упал так, что не успел оформить ошибку по контракту. Показываем
    // человеческий текст, а не «500 Internal Server Error».
    throw new ApiError(response.status, {
      error: {
        code: 'internal_error',
        message: 'Сервер не смог обработать запрос. Попробуйте ещё раз через минуту, а если повторится — сообщите администратору.',
      },
    })
  }

  return payload as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false
  const error = (value as { error?: unknown }).error
  if (typeof error !== 'object' || error === null) return false
  return typeof (error as { code?: unknown }).code === 'string'
}

/** Текст ошибки для показа человеку — из любого исключения, какое прилетело. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof NetworkError) return error.message
  if (error instanceof Error && error.message) return error.message
  return 'Что-то пошло не так. Обновите страницу и повторите действие.'
}

export function errorCode(error: unknown): ErrorCode | undefined {
  return error instanceof ApiError ? error.code : undefined
}
